import type { ReactNode } from "react";
import { useRef } from "react";

import clsx from "clsx";

import { type LikedType, languageSelector, useIdLikedContext } from "@/src/contexts";
import type { AjaxSuccess, Output } from "@Components/Hooks/useAjax";
import useAjax, { AjaxMethod } from "@Components/Hooks/useAjax";
import type { AjaxFail } from "@Components/Modules/Ajax";
import { urlFormat } from "@Components/Modules/Helpers";
import { Follow } from "@Routes/FollowLinks";

import Heart from "../icons/Heart";
import HeartBorder from "../icons/HeartBorder";

import * as css from "./FavoriteLink.module.css";

type FavoriteLinkIconSize = "standard" | "big" | "border";

export enum ClickFromType {
    Undefined,
    Login,
    ForYou,
    SearchResult,
    DiscDetails,
    FlyerDetails,
    FlyerViewer,
    FlyerViewerSearch,
    FlyersWorld,
    Favorites,
    Brands,
    BrandDetails,
    SponsoredBrand,
    Malls,
    MallOffers,
    MallBrands,
    MallDetails,
    MallsAll,
    PredefMallFilter,
    SettingsProfile,
    SettingsForU,
    BrandsAll,
    SpecialEvent,
    BrandsNewOccasions,
    SavedMallOffers,
    SavedDiscounts,
    Map,
    Search,
    About,
    LtLandingPage,
    Ecommerce,
    GenericLandingPage,
    Homepage,
}

interface IFavoriteLink {
    className?: string;
    id: string;
    favoriteLinkIconSize?: FavoriteLinkIconSize;
    showText: boolean;
    type: LikedType;
    success?: AjaxSuccess<Output<boolean>>;
    origin?: ClickFromType;
    fail?: AjaxFail | undefined;
    onClick?: (event: React.MouseEvent<HTMLButtonElement>) => boolean | void;
}

const getFavoriteLinkIconSize = (appearance?: FavoriteLinkIconSize): string => {
    switch (appearance) {
        case "big":
            return css.big;
        case "border":
            return css.border;
        default:
            return "";
    }
};

const getIconSVG = (appearance?: FavoriteLinkIconSize): ReactNode => {
    switch (appearance) {
        case "border":
            return (
                <span className={css.heartBorder}>
                    <HeartBorder />
                </span>
            );
        default:
            return (
                <span className={css.heart}>
                    <Heart />
                </span>
            );
    }
};

export default function FavoriteLink(props: IFavoriteLink) {
    const resources = languageSelector(
        {
            text: "Ulubione",
            ariaLabel: {
                brands: "Obserwuj markę",
                discounts: "Podobnie okazje",
                malls: "Obserwuj centrum handlowe",
            },
        },
        {
            text: "Favourite",
            ariaLabel: {
                brands: "Follow brand",
                discounts: "Like discount",
                malls: "Follow shopping mall",
            },
        },
    );

    if (props.type !== "brands" && props.type !== "malls" && props.type !== "discounts") {
        throw new Error(`Invalid type '${props.type}'`);
    }
    if (!props.id) {
        throw new Error("Id is required!");
    }

    const [isSaved, saveId, unsaveId] = useIdLikedContext(props.id, props.type);
    const favoriteRef = useRef<HTMLButtonElement>(null);

    const onSuccess = (response: Output<boolean>) => {
        if (response?.success !== true) {
            return;
        }

        if (favoriteRef.current && response.data === true) {
            favoriteRef.current.animate(
                {
                    transform: ["scale(1)", "scale(1.4)"],
                    offset: [0, 0.5],
                } as PropertyIndexedKeyframes,
                {
                    duration: 800,
                    direction: "normal",
                    easing: "ease-in-out",
                },
            );
        }

        if (props.success) {
            props.success(response);
        }

        if (response.data) {
            saveId();
        } else {
            unsaveId();
        }
    };

    const { errorContainer, isLoading, request } = useAjax<Output<boolean>>({
        ajaxAction: urlFormat(Follow, { action: props.type }),
        ajaxMethod: AjaxMethod.Post,
        success: onSuccess,
        fail: props.fail,
    });

    const onClick = (event: React.MouseEvent<HTMLButtonElement>): boolean | void => {
        event.preventDefault();
        event.stopPropagation();

        if ((props.onClick && props.onClick(event) === false) || errorContainer) {
            return;
        }

        request(undefined, { follow: !isSaved, id: props.id, origin: props.origin || 0 });
    };

    const compClass = clsx(
        css.favoriteLinkComponent,
        getFavoriteLinkIconSize(props.favoriteLinkIconSize),
        isSaved && css.active,
        isLoading && css.loading,
        props.className,
    );

    return (
        <button
            ref={favoriteRef}
            key={props.id}
            tabIndex={-1}
            className={compClass}
            onClick={onClick}
            title="Like"
            type="button"
            aria-label={resources.ariaLabel[props.type]}
        >
            {getIconSVG(props.favoriteLinkIconSize)}
            {props.showText && <span className={css.text}>{resources.text}</span>}
            {errorContainer}
        </button>
    );
}
